import { z } from 'zod';
import { ITEM_CATEGORIES, ITEM_CONDITIONS } from '../types/item.js';
import { paginationSchema, sortSchema } from './common.js';
// Custom boolean schema that properly handles "true"/"false" strings
const stringBooleanSchema = z.preprocess((val) => {
    if (val === 'true' || val === true)
        return true;
    if (val === 'false' || val === false)
        return false;
    return undefined;
}, z.boolean().optional());
// Item validation schemas
export const itemCategorySchema = z.enum(ITEM_CATEGORIES);
export const itemConditionSchema = z.enum(ITEM_CONDITIONS);
export const purchaseInfoSchema = z.object({
    store: z.string().max(100).optional(),
    approximateDate: z.string().max(50).optional(),
    price: z.number().min(0).optional(),
});
export const itemAttributesSchema = z.object({
    brand: z.string().max(100).optional(),
    model: z.string().max(100).optional(),
    color: z.string().max(50).optional(),
    material: z.string().max(50).optional(),
    size: z.string().max(50).optional(),
    condition: itemConditionSchema.optional(),
    location: z.string().max(100).optional(),
    quantity: z.number().int().min(1).default(1),
    estimatedValue: z.number().min(0).optional(),
    purchaseInfo: purchaseInfoSchema.optional(),
}).passthrough(); // Allow additional attributes
export const createItemSchema = z.object({
    inventoryId: z.string().uuid(),
    title: z
        .string()
        .min(1, 'Title is required')
        .max(200, 'Title must be less than 200 characters')
        .trim(),
    description: z
        .string()
        .max(2000, 'Description must be less than 2000 characters')
        .trim()
        .default(''),
    category: itemCategorySchema,
    condition: itemConditionSchema.default('good'),
    subCategory: z.string().max(100).trim().optional(),
    attributes: itemAttributesSchema.optional().default({}),
    tags: z.array(z.string().max(50)).max(20).default([]),
});
export const updateItemSchema = z.object({
    title: z.string().min(1).max(500).trim().optional(),
    description: z.string().max(10000).trim().optional(),
    location: z.string().max(200).trim().optional().nullable(),
    locationImage: z.string().max(500).optional().nullable(),
    category: itemCategorySchema.optional(),
    condition: itemConditionSchema.optional(),
    subCategory: z.string().max(100).trim().optional().nullable(),
    attributes: itemAttributesSchema.partial().optional(),
    tags: z.array(z.string().max(50)).max(20).optional(),
    listingStatus: z.enum(['private', 'discoverable', 'not_for_sale', 'listed', 'sold']).optional(),
    // Discogs selling fields
    discogsReleaseId: z.number().int().positive().optional().nullable(),
    discogsLabel: z.string().max(2000).optional().nullable(),
    discogsCatno: z.string().max(500).optional().nullable(),
    discogsPrice: z.number().positive().optional().nullable(),
    discogsMediaCondition: z.string().max(100).optional().nullable(),
    discogsSleeveCondition: z.string().max(100).optional().nullable(),
    discogsComments: z.string().max(5000).optional().nullable(),
    // Shipping tracking fields
    shippedAt: z.coerce.date().optional().nullable(),
    deliveredAt: z.coerce.date().optional().nullable(),
    trackingNumber: z.string().max(100).optional().nullable(),
    trackingCarrier: z.string().max(50).optional().nullable(),
    shippingSource: z.enum(['discogs', 'manual']).optional().nullable(),
});
// Item extracted from voice (Realtime API function call)
export const extractedItemSchema = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(2000).default(''),
    category: itemCategorySchema,
    subCategory: z.string().max(100).optional(),
    quantity: z.number().int().min(1).default(1),
    location: z.string().max(100).optional(),
    estimatedValue: z.number().min(0).optional(),
    brand: z.string().max(100).optional(),
    model: z.string().max(100).optional(),
    condition: itemConditionSchema.optional(),
    purchaseInfo: purchaseInfoSchema.optional(),
});
// Item filter/search params
export const itemFilterSchema = paginationSchema.merge(sortSchema).extend({
    inventoryId: z.string().uuid().optional(),
    category: itemCategorySchema.optional(),
    search: z.string().max(200).optional(),
    needsReview: z.coerce.boolean().optional(),
    sourceType: z.enum(['voice', 'manual', 'import']).optional(),
    listingStatus: z.enum(['private', 'discoverable', 'not_for_sale', 'listed', 'sold']).optional(),
    location: z.string().max(200).optional(),
    hasPrice: stringBooleanSchema,
    hasListing: stringBooleanSchema,
    isUnsold: stringBooleanSchema, // Listed but not sold (listingStatus === 'listed' AND soldAt IS NULL)
    needsShipping: stringBooleanSchema, // Sold but not yet shipped (soldAt NOT NULL AND shippedAt IS NULL)
});
//# sourceMappingURL=item.js.map